import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthModal from 'components/auth/AuthModal';
import { DARK, LIGHT } from './landing/themes';
import ParallaxBg from './landing/ParallaxBg';
import Navbar     from './landing/Navbar';
import Hero       from './landing/Hero';
import Features   from './landing/Features';
import HowItWorks from './landing/HowItWorks';
import FAQ        from './landing/FAQ';
import CTABanner  from './landing/CTABanner';
import ContactUs  from './landing/ContactUs';
import Footer     from './landing/Footer';

/**
 * Marketing landing page (the root of techproone.com).
 *
 * Pure orchestrator — holds the theme + scroll + auth-modal state and
 * threads `t`, callbacks, and the modal open/close into the section
 * components. Every visual section lives in its own file under
 * `./landing/` so they can be edited or replaced in isolation.
 *
 * Also the landing spot for a failed social sign-in: the OAuth callback
 * redirects to the dashboard with `?auth=<reason>`, ProtectedRoute bounces
 * an unauthenticated visitor here carrying that param, and we reopen the
 * modal with the reason spelled out instead of silently dumping them back
 * on the marketing page.
 */

// Keyed by the `?auth=` reason the OAuth callback redirects with. Anything
// unrecognised falls back to the generic sentence, so a new backend reason
// never renders as a blank banner.
const AUTH_NOTICES = {
  deactivated: 'That account is deactivated. Contact support to reactivate it.',
  provider_denied: 'The provider did not approve that sign-in. Please try again.',
  no_profile: 'We could not read your profile from that provider. Please try again.',
  no_account_id: 'That provider did not return an account id. Please try again.',
  create_failed: 'We could not finish setting up your account. Please try again.',
  server_error: 'Something went wrong on our side during sign-in. Please try again.',
  failed: 'We could not complete that sign-in. Please try again.',
};

export default function PlatformLanding() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState(0);
  const [authNotice, setAuthNotice] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('tpo_theme');
    return stored ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const t = isDark ? DARK : LIGHT;

  const toggleTheme = () => {
    setIsDark((v) => {
      localStorage.setItem('tpo_theme', v ? 'light' : 'dark');
      return !v;
    });
  };

  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    setScrollY(y);
    setScrolled(y > 20);
  }, []);

  // index.css sets overflow:hidden on html+body for the dashboard —
  // override it for this page only, restore on unmount.
  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Follow OS theme changes unless the user explicitly picked a theme.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (!localStorage.getItem('tpo_theme')) setIsDark(e.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Surface why a social sign-in bounced, then strip the param so a
  // refresh (or a later successful login) doesn't replay the message.
  useEffect(() => {
    const reason = new URLSearchParams(location.search).get('auth');
    if (!reason) return;
    // Show the code alongside the message. The param is stripped from the URL
    // a line below, so without this there is nothing left to tell one failure
    // mode from another when someone reports "it says it failed".
    const message = AUTH_NOTICES[reason] || AUTH_NOTICES.failed;
    setAuthNotice(`${message} (code: ${reason})`);
    setDefaultTab(0);
    setAuthOpen(true);
    navigate('/', { replace: true });
  }, [location.search, navigate]);

  const closeAuth   = () => { setAuthOpen(false); setAuthNotice(null); };
  const openLogin  = () => { setAuthNotice(null); setDefaultTab(0); setAuthOpen(true); };
  const openSignup = () => { setAuthNotice(null); setDefaultTab(1); setAuthOpen(true); };

  return (
    <div style={{ minHeight: '100vh', color: t.text, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", position: 'relative' }}>
      <ParallaxBg  t={t} scrollY={scrollY} />
      <Navbar      t={t} scrolled={scrolled} onLoginClick={openLogin} isDark={isDark} onToggleTheme={toggleTheme} />
      <Hero        t={t} onSignupClick={openSignup} />
      <Features    t={t} />
      <HowItWorks  t={t} />
      <FAQ         t={t} />
      <CTABanner   t={t} onSignupClick={openSignup} />
      <ContactUs   t={t} />
      <Footer      t={t} />
      <AuthModal isOpen={authOpen} onClose={closeAuth} defaultTab={defaultTab} notice={authNotice} />
    </div>
  );
}
